import { Observable } from "rxjs";
import { ConsentPermalinkMasterRef } from "../auth/authServices";

export type MasterDigitalServiceType = "attorney" | "insurance" | "applicant";

export type ConsentPartyType =
  | "Applicant"
  | "AA"
  | "DA"
  | "Insurance"
  | "Employer";

const EMAIL_SENT = "Email Sent";
const ACCEPTED = "Accepted";
const REVOKED = "Revoked";

export function normalizeDigitalServiceStatus(
  status: string | null | undefined
): string {
  if (!status) {
    return "Not Sent";
  }
  const normalized = String(status).trim();
  if (normalized.toLowerCase() === "not sent") {
    return "Not Sent";
  }
  if (normalized.toLowerCase() === "email sent") {
    return EMAIL_SENT;
  }
  if (normalized.toLowerCase() === "accepted") {
    return ACCEPTED;
  }
  if (normalized.toLowerCase() === "revoked") {
    return REVOKED;
  }
  return normalized;
}

export function isDigitalServiceAccepted(
  status: string | null | undefined
): boolean {
  return normalizeDigitalServiceStatus(status) === ACCEPTED;
}

export function isDigitalServiceRevoked(
  status: string | null | undefined
): boolean {
  return normalizeDigitalServiceStatus(status) === REVOKED;
}

/** Drive file id for staff revocation document preview (list row or history tail). */
export const OPT_OUT_DIGITAL_SERVICE_ACTION_LABEL =
  "Opt Out of Digital Service";

export function formatDigitalServiceStatusForDisplay(
  status: string | null | undefined
): string {
  if (isDigitalServiceRevoked(status)) {
    return "Opted Out";
  }
  if (!status) {
    return "Not Sent";
  }
  return String(status).trim();
}

export function getRevocationDriveFileId(record: any): string | null {
  if (!record) {
    return null;
  }
  const direct = record.digitalServiceRevocationDriveFileId;
  if (direct) {
    return String(direct);
  }
  const history = record.digitalServiceRevocationHistory;
  if (Array.isArray(history) && history.length > 0) {
    const last = history[history.length - 1];
    return last?.driveFileId ? String(last.driveFileId) : null;
  }
  return null;
}

const MASTER_CONFIG: Record<
  MasterDigitalServiceType,
  { statusField: string; emailField: string }
> = {
  attorney: {
    statusField: "AttorneyDigitalService",
    emailField: "AttorneyEmail",
  },
  insurance: {
    statusField: "insuranceDigitalService",
    emailField: "insuranceEmail",
  },
  applicant: {
    statusField: "applicantDigitalService",
    emailField: "email",
  },
};

export const ENCOUNTER_CONSENT_PARTY_BY_STATUS_FIELD: Record<
  string,
  { partyType: ConsentPartyType; emailField: string }
> = {
  applicantDigitalService: { partyType: "Applicant", emailField: "email" },
  applicantAttorneyDigitalService: {
    partyType: "AA",
    emailField: "applicantAttorneyEmail",
  },
  defenseDigitalService: { partyType: "DA", emailField: "defenseEmail" },
  employerDigitalService: { partyType: "Employer", emailField: "employerEmail" },
  insuranceDigitalService: {
    partyType: "Insurance",
    emailField: "insuranceEmail",
  },
};

export interface ConsentPermalinkClient {
  getConsentPermalink(
    email: string,
    partyType: string,
    encounterId?: string,
    masterRef?: ConsentPermalinkMasterRef
  ): Observable<{ url: string; invitationId?: string }>;
  recordConsentLinkCopied?(invitationId: string, operationId: string): Observable<unknown>;
}

export interface CopyToast {
  success(message: string, title?: string, options?: object): void;
  error(message: string, title?: string, options?: object): void;
}

function isEmailSentStatus(status: string | null | undefined): boolean {
  if (!status) {
    return false;
  }
  const normalized = status.trim();
  return normalized === EMAIL_SENT || normalized === "Email sent";
}

function hasMasterEmail(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown>
): boolean {
  return getMasterEmail(masterType, record) !== "";
}

export function getMasterEmail(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown> | null | undefined
): string {
  if (!record) {
    return "";
  }
  const { emailField } = MASTER_CONFIG[masterType];
  const email = record[emailField];
  if (email === null || email === undefined) {
    return "";
  }
  return String(email).trim();
}

export function getMasterDigitalServiceStatus(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown> | null | undefined
): string | null {
  if (!record) {
    return null;
  }
  const { statusField } = MASTER_CONFIG[masterType];
  const status = record[statusField];
  if (status === null || status === undefined || status === "") {
    return null;
  }
  return String(status);
}

export function canSendMasterDigitalServiceRequest(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown> | null | undefined
): boolean {
  if (!record || !hasMasterEmail(masterType, record)) {
    return false;
  }

  const status = getMasterDigitalServiceStatus(masterType, record);
  if (!status) {
    return true;
  }

  const normalized = normalizeDigitalServiceStatus(status);
  return (
    normalized === "Not Sent" ||
    normalized === EMAIL_SENT
  );
}

const PI_ATTORNEY_TYPES = new Set(["AA (PI)", "DA (PI)", "AA_PI", "DA_PI"]);
const PI_INSURANCE_CASE_TYPES = new Set([
  "PR4",
  "IME",
  "IMER",
  "IMES",
  "IMED",
  "Department of Labor Case",
  "Longshore Harbor Case",
]);

export function isDigitalServiceExemptMaster(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown> | null | undefined
): boolean {
  if (!record || masterType === "applicant") {
    return masterType === "applicant";
  }
  if (masterType === "attorney") {
    const raw = record["AttorneyType"];
    const types = (Array.isArray(raw) ? raw : String(raw || "").split(","))
      .map((type) => String(type || "").trim().toUpperCase())
      .filter(Boolean);
    return types.length > 0 && types.every((type) => PI_ATTORNEY_TYPES.has(type));
  }
  const caseTypes = record["applicableCaseTypes"];
  const types = Array.isArray(caseTypes) ? caseTypes.map((type) => String(type)) : [];
  return types.length > 0 && types.every((type) => PI_INSURANCE_CASE_TYPES.has(type));
}

export function canManuallyAcceptDigitalService(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown> | null | undefined
): boolean {
  if (!record || !record["_id"] || isDigitalServiceExemptMaster(masterType, record)) {
    return false;
  }
  const status = normalizeDigitalServiceStatus(
    getMasterDigitalServiceStatus(masterType, record)
  );
  return status === "Not Sent" || status === EMAIL_SENT || status === REVOKED;
}

export function canSendNewDigitalServiceFromRevoked(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown> | null | undefined
): boolean {
  if (!record || !hasMasterEmail(masterType, record)) {
    return false;
  }
  return isDigitalServiceRevoked(getMasterDigitalServiceStatus(masterType, record));
}

export function getMasterConsentPartyType(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown> | null | undefined
): ConsentPartyType | null {
  if (!record) {
    return null;
  }

  if (masterType === "insurance") {
    return "Insurance";
  }

  if (masterType === "applicant") {
    return "Applicant";
  }

  const rawTypes = record["AttorneyType"];
  const types = Array.isArray(rawTypes)
    ? rawTypes
    : rawTypes
      ? [rawTypes]
      : [];
  const consentTypes = types.filter(
    (type) => type === "AA" || type === "DA"
  ) as Array<"AA" | "DA">;

  if (consentTypes.length === 1) {
    return consentTypes[0];
  }

  return null;
}

export function canCopyMasterDigitalServiceLink(
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown> | null | undefined
): boolean {
  if (!record) {
    return false;
  }

  if (!isEmailSentStatus(getMasterDigitalServiceStatus(masterType, record))) {
    return false;
  }

  return hasMasterEmail(masterType, record);
}

export function canCopyEncounterConsentPermalink(
  row: Record<string, unknown> | null | undefined,
  statusField: string
): boolean {
  if (!row || !statusField) {
    return false;
  }

  const config = ENCOUNTER_CONSENT_PARTY_BY_STATUS_FIELD[statusField];
  if (!config) {
    return false;
  }

  if (!isEmailSentStatus(row[statusField] as string)) {
    return false;
  }

  const email = getEncounterConsentEmail(row, config);
  return email !== null && email !== undefined && String(email).trim() !== "";
}

function getEncounterConsentEmail(
  row: Record<string, unknown>,
  config: { partyType: ConsentPartyType; emailField: string }
): string {
  const parties = row["partyDigitalService"] as Record<
    string,
    { currentEmail?: string; masterId?: string; reason?: string }
  > | undefined;
  const party = parties?.[config.partyType];
  if (party) {
    return party.masterId && !party.reason ? party.currentEmail || "" : "";
  }
  return String(row[config.emailField] ?? "");
}

export function copyConsentPermalinkToClipboard(
  authService: ConsentPermalinkClient,
  toastr: CopyToast,
  email: string | null | undefined,
  partyType: string,
  onPartyTypeError?: string,
  encounterId?: string,
  masterRef?: ConsentPermalinkMasterRef
): void {
  const normalized = (email || "").trim();
  if (!normalized) {
    toastr.error("No email address available for this party.");
    return;
  }

  if (!partyType) {
    toastr.error(
      onPartyTypeError ||
        "Unable to determine party type for this digital service link."
    );
    return;
  }

  authService
    .getConsentPermalink(normalized, partyType, encounterId, masterRef)
    .subscribe({
      next: (response) => {
        navigator.clipboard.writeText(response.url).then(
          () => {
            if (response.invitationId && partyType !== "Employer" && authService.recordConsentLinkCopied) {
              const bytes = new Uint8Array(20); window.crypto.getRandomValues(bytes);
              const operationId = Array.from(bytes).map((value) => value.toString(16).padStart(2, "0")).join("");
              authService.recordConsentLinkCopied(response.invitationId, operationId).subscribe({
                error: () => toastr.error("Link copied, but the outreach activity could not be recorded."),
              });
            }
            toastr.success("Invite link copied to clipboard", "Copied!", {
              timeOut: 3000,
              closeButton: true,
            });
          },
          () => toastr.error("Unable to copy link to clipboard.")
        );
      },
      error: (error) => {
        toastr.error(
          error?.error?.message || "No active invitation found for this party."
        );
      },
    });
}

export function buildMasterPermalinkRef(
  masterType: MasterDigitalServiceType,
  recordId: string
): ConsentPermalinkMasterRef {
  if (masterType === "attorney") {
    return { attorneyId: recordId };
  }
  if (masterType === "insurance") {
    return { insuranceId: recordId };
  }
  return { applicantId: recordId };
}

export function copyMasterDigitalServiceLink(
  authService: ConsentPermalinkClient,
  toastr: CopyToast,
  masterType: MasterDigitalServiceType,
  record: Record<string, unknown>
): void {
  if (!canCopyMasterDigitalServiceLink(masterType, record)) {
    return;
  }

  const { emailField } = MASTER_CONFIG[masterType];
  const partyType =
    getMasterConsentPartyType(masterType, record) ??
    (masterType === "applicant" ? "Applicant" : "AA");
  const recordId = String(record["_id"] ?? "");
  if (!recordId) {
    toastr.error("Unable to identify this record for the digital service link.");
    return;
  }

  copyConsentPermalinkToClipboard(
    authService,
    toastr,
    String(record[emailField]),
    partyType,
    undefined,
    undefined,
    buildMasterPermalinkRef(masterType, recordId)
  );
}

export function copyEncounterConsentPermalinkForRow(
  authService: ConsentPermalinkClient,
  toastr: CopyToast,
  row: Record<string, unknown>,
  statusField: string
): void {
  if (!canCopyEncounterConsentPermalink(row, statusField)) {
    return;
  }

  const config = ENCOUNTER_CONSENT_PARTY_BY_STATUS_FIELD[statusField];
  const encounterId = String(row["_id"] ?? "");
  if (!encounterId) {
    toastr.error("Unable to identify this encounter for the digital service link.");
    return;
  }
  copyConsentPermalinkToClipboard(
    authService,
    toastr,
    getEncounterConsentEmail(row, config),
    config.partyType,
    undefined,
    encounterId
  );
}
